/**
 * AccordionGroup
 *
 * Wraps an accordion block and, when data-single-open="true" is set,
 * enforces the "only one row open at a time" behavior on its child
 * <details> elements.
 *
 * Strategy: assign a unique shared `name` attribute to every <details>
 * inside this group. The HTML spec then makes the browser auto-close
 * the previously open sibling when another opens — no per-toggle JS
 * required. A defensive `toggle` listener mirrors the behavior on
 * older browsers that don't yet implement <details name>.
 *
 * Timing note: connectedCallback can fire before the parser has
 * appended children. We defer setup to the next animation frame so
 * the children (each accordion-custom -> <details>) are in place.
 */
class AccordionGroup extends HTMLElement {
  connectedCallback() {
    if (this.dataset.singleOpen !== 'true') return;
    requestAnimationFrame(() => this.#setup());
  }

  #setup() {
    const detailsList = this.querySelectorAll('details');
    if (detailsList.length === 0) return;

    const groupName = `accordion-group-${Math.random().toString(36).slice(2, 10)}`;

    detailsList.forEach((d) => {
      d.name = groupName;

      // Fallback for browsers that don't implement <details name>:
      // close siblings on toggle. The native behavior makes this a
      // no-op in supporting browsers (others are already closed).
      d.addEventListener('toggle', () => {
        if (!d.open) return;
        detailsList.forEach((other) => {
          if (other !== d && other.open) other.open = false;
        });
      });
    });
  }
}

if (!customElements.get('accordion-group')) {
  customElements.define('accordion-group', AccordionGroup);
}
